<template>
  <div class="clock-page">
    <v-app-bar app elevation="0" class="clock-app-bar" color="#07111f" dark>
      <div class="d-flex flex-row align-center">
        <DarkBrandLogo
          compact
          class="clock-logo"
          @click.native="$router.go(0)"
        />
      </div>
      <v-spacer></v-spacer>
      <button
        type="button"
        class="socket-status mr-3"
        :class="{ 'socket-status--offline': !socketConnected }"
        :aria-label="socketConnected ? 'Live synchronization connected' : 'Live synchronization disconnected. Reload'"
        @click="handleSocketStatusClick"
      >
        <span class="socket-status__dot"></span>
        <span class="socket-status__copy">
          <strong>{{ socketConnected ? "Live" : "Disconnected" }}</strong>
          <small>{{ socketConnected ? "Tablets synchronized" : "Tap to reload" }}</small>
        </span>
      </button>
      <div class="device-status mr-3">
        <span
          class="device-status__dot"
          :class="{ 'device-status__dot--online': isDeviceConnected }"
        ></span>
        <span class="d-none d-sm-inline">
          {{ isDeviceConnected ? "Fingerprint ready" : "Fingerprint offline" }}
        </span>
      </div>
      <v-menu
        offset-y
        left
        nudge-bottom="8"
        min-width="310"
        max-width="340"
        content-class="home-settings-menu"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            large
            class="menu-button"
            v-bind="attrs"
            v-on="on"
            aria-label="Open tablet menu"
          >
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <v-list dark class="home-menu-list">
          <div class="home-menu-heading">
            <span class="home-menu-heading__icon">
              <v-icon small>mdi-tablet-dashboard</v-icon>
            </span>
            <div>
              <strong>Tablet menu</strong>
              <small>Location and support options</small>
            </div>
          </div>

          <v-divider class="home-menu-divider"></v-divider>

          <v-list-item
            class="home-menu-item"
            @click.stop.prevent="switchLocation"
          >
            <span class="home-menu-item__icon">
              <v-icon>mdi-map-marker-outline</v-icon>
            </span>
            <div class="home-menu-item__copy">
              <strong>Switch location</strong>
              <small>
                Connect this tablet to another store
              </small>
            </div>
            <v-list-item-action>
              <v-icon small>mdi-chevron-right</v-icon>
            </v-list-item-action>
          </v-list-item>

          <v-list-item
            class="home-menu-item"
            @click.stop.prevent="openAnyDeskSupport"
          >
            <span class="home-menu-item__icon">
              <v-icon>mdi-lifebuoy</v-icon>
            </span>
            <div class="home-menu-item__copy">
              <strong>AnyDesk support</strong>
              <small>
                Open AnyDesk and share the address shown
              </small>
            </div>
            <v-list-item-action>
              <v-icon small>mdi-open-in-new</v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <section
      class="clock-hero"
      :class="{
        'clock-hero--with-camera':
          metadata &&
          metadata.customClockIn.storeTimeClockImage &&
          metadata.customClockIn.storeTimeClockImageView,
      }"
    >
      <aside
        v-if="
          metadata &&
          metadata.customClockIn.storeTimeClockImage &&
          metadata.customClockIn.storeTimeClockImageView
        "
        class="camera-preview"
      >
        <div class="camera-preview__frame">
          <WebCam
            ref="webcam"
            width="220"
            height="140"
            class="camera-preview__video"
          ></WebCam>
          <div class="camera-preview__live">
            <span></span>
            Camera on
          </div>
        </div>
        <div class="camera-preview__notice">
          <span class="camera-preview__notice-icon">
            <v-icon small>mdi-camera-outline</v-icon>
          </span>
          <div>
            <strong>Photo capture is on</strong>
            <small>Your photo is taken when you clock in or out.</small>
          </div>
        </div>
      </aside>

      <div
        v-else-if="metadata && metadata.customClockIn.storeTimeClockImage"
        class="camera-capture-hidden"
        aria-hidden="true"
      >
        <WebCam
          ref="webcam"
          width="120"
          height="120"
        ></WebCam>
      </div>

      <div class="clock-hero__content">
        <div class="clock-hero__eyebrow">
          <v-icon small color="#60a5fa" class="mr-1"
            >mdi-map-marker-outline</v-icon
          >
          {{ location ? location.name : "Your location" }}
        </div>
        <div id="clock" class="text-center">
          <div class="clock-hero__time">{{ todayDate | moment("LT") }}</div>
          <div class="clock-hero__date">
            {{ todayDate | moment("dddd, MMMM D, YYYY") }}
          </div>
        </div>
        <div class="clock-hero__hint">Tap your name below to get started</div>
      </div>
    </section>

    <div class="employee-status-summary" aria-label="Employee clock status summary">
      <div class="employee-status-summary__item employee-status-summary__item--total">
        <span class="employee-status-summary__icon">
          <v-icon small>mdi-account-group</v-icon>
        </span>
        <span>
          <strong>{{ employees.length }}</strong>
          <small>Total employees</small>
        </span>
      </div>
      <div class="employee-status-summary__item employee-status-summary__item--in">
        <span class="employee-status-summary__icon">
          <v-icon small>mdi-account-check</v-icon>
        </span>
        <span>
          <strong>{{ clockedInCount }}</strong>
          <small>Clocked in</small>
        </span>
      </div>
      <div class="employee-status-summary__item employee-status-summary__item--out">
        <span class="employee-status-summary__icon">
          <v-icon small>mdi-account-off</v-icon>
        </span>
        <span>
          <strong>{{ clockedOutCount }}</strong>
          <small>Clocked out</small>
        </span>
      </div>
    </div>

    <div v-if="$vuetify.breakpoint.smAndDown" class="alphabet-search">
      <div class="alphabet-strip noscroll">
        <button type="button" @click="alfClcik(al)" v-for="(al, index) in alf" :key="index"
          :class="{ 'alphabet-letter--active': search.toUpperCase().includes(al) }">
          {{ al }}
        </button>
      </div>
      <div v-if="search" class="search-feedback">
        <span><v-icon small>mdi-magnify</v-icon>Matching <strong>{{ search }}</strong></span>
        <span>{{ filteredItems.length }} found</span>
        <button type="button" @click="backString" aria-label="Remove last search letter"><v-icon small>mdi-backspace-outline</v-icon></button>
        <button type="button" @click="clearBtn" aria-label="Clear search"><v-icon small>mdi-close</v-icon></button>
      </div>
    </div>

    <v-container class="employee-directory py-0" grid-list-lg>
      <v-row>
        <v-col cols="2" lg="1" v-if="!$vuetify.breakpoint.smAndDown">
          <div
            class="alphabet-rail"
            :class="{
              'touch-view-container': $vuetify.breakpoint.smAndDown,
              'touch-view-container-big': !$vuetify.breakpoint.smAndDown,
            }"
          >
            <v-list class="pa-0">
              <v-list-item
                class="alphabet-rail__item"
                :class="{ 'alphabet-letter--active': search.toUpperCase().includes(al) }"
                @click="alfClcik(al)"
                v-for="(al, index) in alf"
                :key="index"
              >
                <v-list-item-content>
                  <v-list-item-title class="text-center">{{
                    al
                  }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>
        </v-col>
        <v-col>
          <v-card
            outlined
            width="100%"
            :class="{
              'touch-view-container': $vuetify.breakpoint.xsOnly,
              'touch-view-container-big': !$vuetify.breakpoint.xsOnly,
            }"
            class="employee-panel rounded-xl"
          >
            <v-card-text class="pa-2 pa-sm-4">
              <v-list two-line class="employee-list">
                <!-- <v-card color="primary" class="mt-2">
                  <v-chip class="my-2 font-weight-bold" label color="primary" text-color="white">{{
                    department.departmentName }}</v-chip>
                </v-card> -->
                <div v-if="showDepartment && showDepartmentSetting">
                  <div
                    v-for="department in filteredItems"
                    :key="department.departmentName"
                    class="department-section"
                  >
                    <div class="department-heading">
                      <span class="department-heading__icon">
                        <v-icon small>mdi-account-group</v-icon>
                      </span>
                      <div class="department-heading__copy">
                        <small>Department</small>
                        <strong>{{ department.departmentName }}</strong>
                      </div>
                      <span class="department-heading__count">
                        {{ department.employees.length }}
                        {{
                          department.employees.length === 1
                            ? "employee"
                            : "employees"
                        }}
                      </span>
                    </div>
                    <template v-for="(employee, index) in department.employees">
                      <v-list-item
                        class="employee-row"
                        :class="[
                          index % 2 === 0
                            ? 'even-background'
                            : 'odd-background',
                        ]"
                        @click.stop.prevent="setSelectedEmployee(employee)"
                      >
                        <v-list-item-avatar
                          class="fingerprint-avatar fingerprint-avatar--enrolled"
                          v-if="isFingerprintEnrolled(employee.metadata)"
                        >
                          <v-icon>mdi-fingerprint</v-icon>
                        </v-list-item-avatar>
                        <v-list-item-avatar
                          class="fingerprint-avatar fingerprint-avatar--inactive"
                          v-else
                        >
                          <v-icon>mdi-fingerprint-off</v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title class="text-h6 text-capitalize">
                            <span v-for="(segment, segmentIndex) in nameSegments(employee.name)" :key="segmentIndex"
                              :class="{ 'name-match': segment.match }">{{ segment.text }}</span>
                          </v-list-item-title>
                          <v-list-item-subtitle
                            v-if="employee.TimeClocks.length !== 0"
                          >
                            <div
                              v-if="
                                employee.TimeClocks[0].status === 'clockedIn' ||
                                employee.TimeClocks[0].status === 'breakStart'
                              "
                            >
                              {{
                                employee.TimeClocks[0].clockInTime
                                  | moment("ddd")
                              }}
                              -
                              {{
                                employee.TimeClocks[0].clockInTime
                                  | moment("M/D/YY h:mm A")
                              }}
                            </div>
                            <div v-else>
                              {{
                                employee.TimeClocks[0].clockOutTime
                                  | moment("ddd")
                              }}
                              -
                              {{
                                employee.TimeClocks[0].clockOutTime
                                  | moment("M/D/YY h:mm A")
                              }}
                            </div>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action-text
                          v-if="employee.TimeClocks.length === 0"
                        >
                          Never
                        </v-list-item-action-text>
                        <v-list-item-action-text v-else class="text-right">
                          <!-- when employee is clocked in  -->
                          <div
                            class="font-weight-bold green--text text-right"
                            v-if="employee.TimeClocks[0].status === 'clockedIn'"
                          >
                            <v-chip color="green" text-color="white">
                              IN
                              <v-avatar right class="green darken-4">
                                <v-icon small
                                  >mdi-location-enter</v-icon
                                ></v-avatar
                              >
                            </v-chip>
                          </div>

                          <!-- when employee on break -->
                          <div
                            class="font-weight-bold green--text text-right"
                            v-else-if="
                              employee.TimeClocks[0].status === 'breakStart'
                            "
                          >
                            <v-chip color="yellow darken-3" text-color="white">
                              On Break
                              <v-avatar right class="yellow darken-4">
                                <v-icon small>mdi-coffee-to-go-outline</v-icon>
                              </v-avatar>
                            </v-chip>
                          </div>

                          <!-- when employee is clocked out  -->
                          <div class="font-weight-bold red--text" v-else>
                            <v-chip color="red" text-color="white">
                              OUT
                              <v-avatar right class="red darken-4">
                                <v-icon small
                                  >mdi-location-exit</v-icon
                                ></v-avatar
                              >
                            </v-chip>
                          </div>

                          <!-- show clock out time only when its clocked in or on break -->
                          <div
                            v-if="
                              employee.TimeClocks[0].status === 'clockedIn' ||
                              employee.TimeClocks[0].status === 'breakstart'
                            "
                            class="caption"
                          >
                            {{
                              employee.TimeClocks[0].clockInTime
                                | moment("from", "now")
                            }}
                          </div>
                        </v-list-item-action-text>
                      </v-list-item>
                      <v-divider
                        v-if="index + 1 < employees.length"
                        :key="index"
                      ></v-divider>
                    </template>
                  </div>
                </div>

                <div v-else>
                  <template v-for="(employee, index) in filteredItems">
                    <v-list-item
                      class="employee-row"
                      :class="[
                        index % 2 === 0 ? 'even-background' : 'odd-background',
                      ]"
                      @click.stop.prevent="setSelectedEmployee(employee)"
                    >
                      <v-list-item-avatar
                        class="fingerprint-avatar fingerprint-avatar--enrolled"
                        v-if="isFingerprintEnrolled(employee.metadata)"
                      >
                        <v-icon>mdi-fingerprint</v-icon>
                      </v-list-item-avatar>
                      <v-list-item-avatar
                        class="fingerprint-avatar fingerprint-avatar--inactive"
                        v-else
                      >
                        <v-icon>mdi-fingerprint-off</v-icon>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title class="text-h6 text-capitalize">
                          <span v-for="(segment, segmentIndex) in nameSegments(employee.name)" :key="segmentIndex"
                            :class="{ 'name-match': segment.match }">{{ segment.text }}</span>
                        </v-list-item-title>
                        <v-list-item-subtitle
                          v-if="employee.TimeClocks.length !== 0"
                        >
                          <div
                            v-if="
                              employee.TimeClocks[0].status === 'clockedIn' ||
                              employee.TimeClocks[0].status === 'breakStart'
                            "
                          >
                            {{
                              employee.TimeClocks[0].clockInTime | moment("ddd")
                            }}
                            -
                            {{
                              employee.TimeClocks[0].clockInTime
                                | moment("M/D/YY h:mm A")
                            }}
                          </div>
                          <div v-else>
                            {{
                              employee.TimeClocks[0].clockOutTime
                                | moment("ddd")
                            }}
                            -
                            {{
                              employee.TimeClocks[0].clockOutTime
                                | moment("M/D/YY h:mm A")
                            }}
                          </div>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action-text
                        v-if="employee.TimeClocks.length === 0"
                      >
                        Never
                      </v-list-item-action-text>
                      <v-list-item-action-text v-else class="text-right">
                        <!-- when employee is clocked in  -->
                        <div
                          class="font-weight-bold green--text text-right"
                          v-if="employee.TimeClocks[0].status === 'clockedIn'"
                        >
                          <v-chip color="green" text-color="white">
                            IN
                            <v-avatar right class="green darken-4">
                              <v-icon small
                                >mdi-location-enter</v-icon
                              ></v-avatar
                            >
                          </v-chip>
                        </div>

                        <!-- when employee on break -->
                        <div
                          class="font-weight-bold green--text text-right"
                          v-else-if="
                            employee.TimeClocks[0].status === 'breakStart'
                          "
                        >
                          <v-chip color="yellow darken-3" text-color="white">
                            On Break
                            <v-avatar right class="yellow darken-4">
                              <v-icon small>mdi-coffee-to-go-outline</v-icon>
                            </v-avatar>
                          </v-chip>
                        </div>

                        <!-- when employee is clocked out  -->
                        <div class="font-weight-bold red--text" v-else>
                          <v-chip color="red" text-color="white">
                            OUT
                            <v-avatar right class="red darken-4">
                              <v-icon small>mdi-location-exit</v-icon></v-avatar
                            >
                          </v-chip>
                        </div>

                        <!-- show clock out time only when its clocked in or on break -->
                        <div
                          v-if="
                            employee.TimeClocks[0].status === 'clockedIn' ||
                            employee.TimeClocks[0].status === 'breakstart'
                          "
                          class="caption"
                        >
                          {{
                            employee.TimeClocks[0].clockInTime
                              | moment("from", "now")
                          }}
                        </div>
                      </v-list-item-action-text>
                    </v-list-item>
                    <v-divider
                      v-if="index + 1 < filteredItems.length"
                      :key="index"
                    ></v-divider>
                  </template>
                </div>
              </v-list>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <CSPLoader v-if="loading" />

    <ChangePin
      :changeEmployee="selectedEmployee"
      :dialog="changePinDialog"
      @close="changePinDialog = !changePinDialog"
    />

    <PinVerificationDialog
      v-model="showPinVerify"
      :employee="selectedEmployee"
      @verifyPin="handlePinVerification"
    />

    <FingerprintEnrollmentDialog
      v-if="!supportsNativeFingerprintEnrollment"
      v-model="showEnrollment"
      :employee="selectedEmployee"
      @enrolled="handleEnrollmentComplete"
    />

    <v-dialog v-model="statusCheckDialog" persistent max-width="460">
      <v-card elevation="20" class="action-dialog">
        <v-btn
          icon
          class="action-dialog__close"
          @click="statusCheckDialog = !statusCheckDialog"
          aria-label="Close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-card-text class="pa-6 pa-sm-8">
          <div class="action-dialog__eyebrow">Time clock</div>
          <div class="action-dialog__title">
            Hi{{ selectedEmployee ? `, ${selectedEmployee.name}` : "" }}
          </div>
          <div class="action-dialog__subtitle">What would you like to do?</div>

          <v-row class="mt-5" dense>
            <v-col cols="12" sm="6">
              <button
                type="button"
                class="clock-action clock-action--in"
                @click="setClockInOrOut('clockedIn')"
              >
                <span class="clock-action__icon">
                  <v-icon color="#34d399">mdi-login</v-icon>
                </span>
                <span>
                  <strong>Clock In</strong>
                  <small>Start your shift</small>
                </span>
                <v-icon class="clock-action__arrow">mdi-chevron-right</v-icon>
              </button>
            </v-col>
            <v-col cols="12" sm="6">
              <button
                type="button"
                class="clock-action clock-action--out"
                @click="setClockInOrOut('clockedOut')"
              >
                <span class="clock-action__icon">
                  <v-icon color="#fb7185">mdi-logout</v-icon>
                </span>
                <span>
                  <strong>Clock Out</strong>
                  <small>End your shift</small>
                </span>
                <v-icon class="clock-action__arrow">mdi-chevron-right</v-icon>
              </button>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <PinEntry
      :metadata="metadata"
      :integration="integration"
      :locationCode="locationCode"
      @closeSubmit="closeSubmit"
      :pinDialog="pinDialog"
      @close="pinClose"
      :selectedEmployee="selectedEmployee"
    />

    <v-dialog v-model="loadingClockIn" persistent width="300">
      <v-card color="primary" class="rounded-lg" dark>
        <v-card-text class="py-6">
          Please stand by
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- <Submit v-if="submitDialog" :setting="integration" :employeeImage="image" :status="status" :action="action"
      :dialog="submitDialog" @close="closeSubmit" :employee="selectedEmployee" /> -->

    <TimeClockAction
      v-if="TimeClockActionDialog"
      @cancel="closeTimeClockAction"
      @submit="submitTimeClockAction"
      :employee="actionEmployee"
      :modal="TimeClockActionDialog"
    />

    <ClockStatusOverlay
      :visible="showStatusOverlay"
      :employeeName="overlayEmployeeName"
      :status="overlayStatus"
    />
  </div>
</template>

<script>
import authService from "@/modules/Authentication/service.js";
import TimeClockAction from "@/modules/TimeClock/components/TimeClockActionDialog.vue";
import publicService from "@/api/public";
import FingerprintEnrollmentDialog from "@/modules/TimeClock/components/FingerprintEnrollmentDialog.vue";
import PinVerificationDialog from "@/modules/TimeClock/components/PinVerificationDialog.vue";
import employeeService from "@/modules/Employee/service";
import timeClockService from "@/modules/TimeClock/service.js";
// import Appbar from "@/components/Appbar.vue";
import { mapGetters, mapActions } from "vuex";
const pjson = require("../../../package.json");
import ChangePin from "@/modules/Employee/components/ChangePin.vue";
import PinEntry from "@/modules/TimeClock/components/PinEntry.vue";
const { validationMixin } = require("vuelidate");
const { required } = require("vuelidate/lib/validators");
import { WebCam } from "vue-cam-vision";
import ClockStatusOverlay from "@/components/ClockStatusOverlay.vue";
import DarkBrandLogo from "@/components/DarkBrandLogo.vue";
// import settingService from "@/modules/Settings/service.js";
// import Submit from "@/modules/TimeClock/components/submit.vue";
// import NoSleep from "nosleep.js";

export default {
  name: "clockInOut",
  data() {
    return {
      showStatusOverlay: false,
      overlayEmployeeName: "",
      overlayStatus: "", // 'in' or 'out'
      isEnrolling: false,
      returnMessage: null,
      showPinVerify: false,
      showEnrollment: false,
      supportsNativeFingerprintEnrollment: false,
      isDeviceConnected: false,
      isAndroidApp: false,
      showDepartment: false,
      showDepartmentSetting: false,
      clockInOrOut: null,
      statusCheckDialog: false,
      pinDialog: false,
      TimeClockActionDialog: false,
      action: null,
      actionEmployee: null,
      snackbar: false,
      groupedEmployees: [],
      integration: null,
      loadingClockIn: false,
      search: "",
      alf: [
        "A",
        "B",
        "C",
        "D",
        "E",
        "F",
        "G",
        "H",
        "I",
        "J",
        "K",
        "L",
        "M",
        "N",
        "O",
        "P",
        "Q",
        "R",
        "S",
        "T",
        "U",
        "V",
        "W",
        "X",
        "Y",
        "Z",
      ],
      submitDialog: false,
      changePinDialog: false,
      interval: null,
      statusRefreshInterval: null,
      employeeRefreshTimeout: null,
      appUpdateInterval: null,
      employeeListRequestId: 0,
      socketConnected: false,
      todayDate: null,
      pointerMode: "PointerEvent" in window,
      loading: false,
      pinError: false,
      pinCodeCircles: [1, 2, 3, 4],
      timeClock: {
        EmployeeId: null,
        pinCode: null,
      },
      noSleep: null,
      status: null,
      pin: "",
      location: null,
      locationCode: null,
      deviceCode: null,
      deviceName: null,
      employees: [],
      selectedEmployee: null,
      numbers: [1, 2, 3, 4, 5, 6, 7, 8, 9],
      metadata: {
        customClockIn: {
          storeTimeClockImage: false,
          storeTimeClockImageView: false,
        },
      },
      image: null,
    };
  },
  beforeDestroy() {
    // prevent memory leak
    clearInterval(this.interval);
    clearInterval(this.statusRefreshInterval);
    clearInterval(this.appUpdateInterval);
    clearTimeout(this.employeeRefreshTimeout);
    window.removeEventListener(
      "timeclock-app-update-available",
      this.handleAppUpdateAvailable
    );
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    window.removeEventListener("focus", this.handleWindowFocus);
    // this.noSleep.disable();
  },
  components: {
    // Submit,
    ChangePin,
    WebCam,
    PinVerificationDialog,
    TimeClockAction,
    FingerprintEnrollmentDialog,
    ClockStatusOverlay,
    DarkBrandLogo,
    PinEntry,
  },
  async created() {
    this.socketConnected = Boolean(this.$socket && this.$socket.connected);
    window.addEventListener(
      "timeclock-app-update-available",
      this.handleAppUpdateAvailable
    );
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    window.addEventListener("focus", this.handleWindowFocus);
    this.appUpdateInterval = setInterval(this.applyAppUpdateWhenIdle, 5000);
    if (typeof window.AndroidApp !== "undefined") {
      this.isAndroidApp = true;
      try {
        this.supportsNativeFingerprintEnrollment =
          typeof window.AndroidApp.getBridgeVersion === "function" &&
          Number(window.AndroidApp.getBridgeVersion()) >= 2;
      } catch (error) {
        this.supportsNativeFingerprintEnrollment = false;
      }
      this.message = "✅ Running inside Android WebView";
    } else {
      this.isAndroidApp = false;
      this.message = "🌐 Running in normal browser";
    }
    window.onDeviceStatusChanged = (status) => {
      this.isDeviceConnected = status;
      // console.log("Device status:", status ? "connected" : "disconnected");
      this.message = status
        ? "✅ Fingerprint device ready to capture"
        : "⚠️ Fingerprint device disconnected";
    };

    this.todayDate = this.$moment();

    this.interval = setInterval(() => {
      this.todayDate = this.$moment();
      if (this.todayDate.format("hhmm") === "1100") {
        this.$router.go();
      }
    }, 60000);

    if (!localStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX}code`)) {
      this.$router.push({ name: "authentication-find-location" });
    }

    if (this.$route.params & this.$route.params.locationCode) {
      this.locationCode = this.$route.params.locationCode;
      this.deviceName = this.$route.params.deviceName;
    } else if (
      localStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX}code`)
    ) {
      this.locationCode = localStorage.getItem(
        `${process.env.VUE_APP_STORAGE_PREFIX}code`
      );
    }

    this.joinSocketChannels();
    let location = localStorage.getItem(
      `${process.env.VUE_APP_STORAGE_PREFIX}location`
    );
    if (location) {
      this.location = JSON.parse(location);
    }

    const { deviceName, deviceCode } = authService.getStoredDevice();
    this.deviceName = deviceName;
    this.deviceCode = deviceCode;
    if (
      !localStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX}deviceCode`)
    ) {
      localStorage.setItem(
        `${process.env.VUE_APP_STORAGE_PREFIX}deviceCode`,
        this.deviceCode
      );
    }

    // this.$nextTick(() => {
    //   this.noSleep = new NoSleep();
    //   this.noSleep.enable();
    // });

    await this.loadSetting();
    await this.getEmployeesList();
    this.statusRefreshInterval = setInterval(() => {
      if (!document.hidden) this.getEmployeesList();
    }, 15000);
  },
  mounted() {
    // ✅ Handle real-time fingerprint match directly from Android
    window.onFingerprintMatch = async (data) => {
      const parsed = typeof data === "string" ? JSON.parse(data) : data;
      if (!parsed) return;

      const { id } = parsed;
      const emp = this.employees?.find((e) => e.id === id) || null;

      if (!emp) {
        return;
      }

      this.selectedEmployee = emp;

      // 🔥 Automatically perform clock in/out
      await this.autoClockInOut(emp);
    };

    // ✅ Fallback: handle base64 if Android didn’t match locally
    window.onFingerprintScan = async () => {
      if (this.isEnrolling) return;
      if (this.supportsNativeFingerprintEnrollment) return;

      this.$toast.info("Fingerprint scanned, but no enrolled employee matched.", {
        timeout: 2000,
        closeOnClick: false,
        pauseOnHover: false,
        pauseOnFocusLoss: false,
        draggable: false,
      });

      clearTimeout(this.fingerprintTimer);
      // console.log("is running", this.isAndroidApp);
      // this.fingerprintTimer = setTimeout(() => {
      //   this.handleFingerprintScan(base64);
      // }, 300);
    };

    window.__setEnrolling = (status) => {
      this.isEnrolling = Boolean(status);
    };

    if (this.supportsNativeFingerprintEnrollment) {
      window.onFingerprintEnrollment = (fingerprintTemplate) => {
        this.handleEnrollmentComplete(fingerprintTemplate);
      };
    }
  },
  computed: {
    ...mapGetters("global", ["employeeList", "getTimeClockUsers"]),
    filteredItems() {
      if (this.search) {
        return this.employees.filter((item) => {
          return (
            item.name.toLowerCase().indexOf(this.search.toLowerCase()) > -1
          );
        });
      } else {
        if (
          !this.groupedEmployees.length ||
          !this.showDepartmentSetting
        ) {
          return this.employees;
        } else {
          return this.groupedEmployees;
        }
      }
    },
    version() {
      // console.log('pjson', pjson.version)
      return pjson.version;
    },
    timeClockStatus() {
      let timeClockStatus = "Clock In/Out";
      if (this.status === "clockedIn") return "Clock Out";
      if (this.status === "clockedOut") return "Clock In";
      if (this.status === "breakStart") return "End Break";
      if (this.status === "breakEnd") return "Start Break";
      return timeClockStatus;
    },
    clockedInCount() {
      return this.employees.filter((employee) =>
        ["clockedIn", "breakStart"].includes(employee.status)
      ).length;
    },
    clockedOutCount() {
      return this.employees.length - this.clockedInCount;
    },
  },
  sockets: {
    connect() {
      this.socketConnected = true;
      // Make sure to rejoin the channels
      this.joinSocketChannels();
    },
    disconnect() {
      this.socketConnected = false;
    },
    connect_error() {
      this.socketConnected = false;
    },
    reconnect_failed() {
      this.socketConnected = false;
    },
    notification() {},
    message() {},
    employeeStatusChange() {
      clearTimeout(this.employeeRefreshTimeout);
      this.employeeRefreshTimeout = setTimeout(() => {
        this.getEmployeesList();
      }, 500);
    },
    settingChange() {
      // this.loadSetting();
      this.$router.go();
    },
  },
  watch: {
    search(val) {
      if (val.length === 0) {
        this.showDepartment = true;
        this.snackbar = false;
      } else {
        this.showDepartment = false;
      }
      // console.log('search', val, this.showDepartment, this.filteredItems)
    },
    async pin() {
      // Reseting Pin
      if (this.pin.length === 5) {
        this.pin = "";
      }

      // Submiting
      if (this.pin.length === 4) {
        if (this.metadata && this.metadata.customClockIn.storeTimeClockImage) {
          this.loadingClockIn = true;
          this.image = await this.getImage();
          if (this.image) this.image.status = this.status;
        }
        if (this.checkToken()) {
          // console.log("have token", this.checkToken());
          this.clockInOutEmployee();
        } else {
          // console.log("no token");
          this.submitWithClockIn();
        }
      }
    },
  },
  methods: {
    ...mapActions("global", [
      "setEmployeeList",
      "setTimeClockAdmins",
      "setTimeClockEmployees",
    ]),
    handleSocketStatusClick() {
      if (!this.socketConnected) window.location.reload();
    },
    handleVisibilityChange() {
      if (!document.hidden) this.getEmployeesList();
    },
    handleWindowFocus() {
      this.getEmployeesList();
    },
    handleAppUpdateAvailable() {
      this.applyAppUpdateWhenIdle();
    },
    applyAppUpdateWhenIdle() {
      if (!window.__TIME_CLOCK_PENDING_UPDATE__) return;

      const clockActionInProgress =
        this.loading ||
        this.loadingClockIn ||
        this.pin.length > 0 ||
        this.pinDialog ||
        this.statusCheckDialog ||
        this.TimeClockActionDialog ||
        this.changePinDialog ||
        this.showPinVerify ||
        this.showEnrollment ||
        this.isEnrolling;

      if (
        !clockActionInProgress &&
        typeof window.__ACTIVATE_TIME_CLOCK_UPDATE__ === "function"
      ) {
        window.__ACTIVATE_TIME_CLOCK_UPDATE__();
      }
    },

    async autoClockInOut(employee) {
      try {
        if (!employee || !employee.id) {
          return;
        }

        const { deviceName, deviceCode } = authService.getStoredDevice();
        const pin = employee.pinCode;
        const locationId = employee.LocationEmployee?.LocationId;
        const locationCode = this.locationCode;

        if (!pin) {
          this.$toast.error("No PIN found for this employee.");
          return;
        }

        const timeClock = {
          EmployeeId: employee.id,
          pinCode: pin,
          LocationId: locationId,
        };

        const isClockedIn = employee.TimeClocks?.[0]?.status === "clockedIn";

        // Overlay helper
        const showOverlay = (emp, status) => {
          this.overlayEmployeeName = emp.name;
          this.overlayStatus = status;
          this.showStatusOverlay = true;

          setTimeout(() => {
            this.showStatusOverlay = false;
          }, 3000);
        };

        // ==============================================
        // 1. TEST TOKEN (NEW FIX)
        // ==============================================
        let tokenIsValid = false;

        const token = localStorage.getItem(
          `${process.env.VUE_APP_STORAGE_PREFIX}token`
        );

        if (token) {
          try {
            // Call any small endpoint that requires authentication
            await authService.verify(); // or getProfile(), whatever exists in your service
            tokenIsValid = true;
          } catch (e) {
            tokenIsValid = false;
            localStorage.removeItem(
              `${process.env.VUE_APP_STORAGE_PREFIX}token`
            );
          }
        }

        // ==============================================
        // 2. If token valid → clock in/out
        // ==============================================
        if (tokenIsValid) {
          if (isClockedIn) {
            const result = await timeClockService.clockOut({
              ...timeClock,
              clockOutTime: this.$moment().toISOString(),
              clockOutFingerprint: true,
            });

            if (result) {
              showOverlay(employee, "out");
              this.selectedEmployee.status = "clockedOut";
            }
          } else {
            const result = await timeClockService.clockIn({
              ...timeClock,
              clockInTime: this.$moment().toISOString(),
              clockInNote: "Clocked In via Fingerprint",
              clockInFingerprint: true,
            });

            if (result) {
              showOverlay(employee, "in");
              this.selectedEmployee.status = "clockedIn";
            }
          }

          // ==============================================
          // 3. No valid token → authenticate first
          // ==============================================
        } else {
          const loginInfo = {
            id: employee.id,
            pin,
            locationCode,
            deviceCode,
            deviceName,
          };

          await authService.authenticate(loginInfo);
          await authService.setLastEmployeeId(employee.id);

          if (isClockedIn) {
            const result = await timeClockService.clockOut({
              EmployeeId: employee.id,
              pinCode: pin,
              clockOutTime: this.$moment().toISOString(),
              clockOutFingerprint: true,
            });

            if (result) {
              showOverlay(employee, "out");
              this.selectedEmployee.status = "clockedOut";
            }
          } else {
            const result = await timeClockService.clockIn({
              EmployeeId: employee.id,
              pinCode: pin,
              clockInTime: this.$moment().toISOString(),
              clockInNote: "Clocked In via Fingerprint",
              clockInFingerprint: true,
            });

            if (result) {
              showOverlay(employee, "in");
              this.selectedEmployee.status = "clockedIn";
            }
          }
        }

        // ==============================================
        // Finish up
        // ==============================================
        this.loadingClockIn = false;
        this.openSubmitDialog = true;
        await this.getEmployeesList();
      } catch (error) {
        this.loadingClockIn = false;

        this.$toast.error(error.data?.message || "Auto clock in/out failed.", {
          timeout: 2500,
        });

        localStorage.removeItem(`${process.env.VUE_APP_STORAGE_PREFIX}token`);
      }
    },

    async handleFingerprintScan(base64) {
      // If running in Android app, we already get match results from Android
      if (this.isAndroidApp) {
        return;
      }

      // Only run this block when in browser mode (no Android)
      try {
        this.$toast.info("Browser mode: verifying fingerprint via backend...");

        const payload = {
          template: base64,
          OrganizationId: this.location?.OrganizationId,
        };

        const res = await publicService.matchFingerprint(payload);
        const result = res.data || res;

        if (result.success && result.employee) {
          this.$swal.fire({
            title: "✅ Match Found (Server)",
            html: `
          <div style="text-align:left; line-height:1.6;">
            <strong>Name:</strong> ${result.employee.name || "Unknown"}<br/>
            <strong>ID:</strong> ${result.employee.id}<br/>
            <strong>Pin:</strong> ${result.employee.pinCode}<br/>
            <strong>Score:</strong> ${
              result.score ? result.score.toFixed(2) : "N/A"
            }
          </div>
        `,
            icon: "success",
          });

          this.selectedEmployee = result.employee;
        } else {
          this.$swal.fire({
            title: "No Match Found",
            text: "This fingerprint does not match any enrolled employee.",
            icon: "error",
          });
        }
      } catch (err) {
        this.$toast.error("Error while matching fingerprint");
      }
    },
    openPinVerificationDialog() {
      this.showPinVerify = true;
    },
    async handleEnrollmentComplete(templates) {
      try {
        let fingerprintTemplate = "";

        // Handle both old (array) and new (single string) formats
        if (Array.isArray(templates)) {
          fingerprintTemplate = templates[0]?.trim() || "";
        } else if (typeof templates === "string") {
          fingerprintTemplate = templates.trim();
        } else {
          if (this.supportsNativeFingerprintEnrollment) {
            window.AndroidApp.completeFingerprintEnrollmentSave(
              false,
              "The fingerprint data was invalid. Please try again."
            );
          } else {
            this.$toast.error("Invalid fingerprint enrollment data format.");
          }
          return;
        }

        if (!fingerprintTemplate) {
          if (this.supportsNativeFingerprintEnrollment) {
            window.AndroidApp.completeFingerprintEnrollmentSave(
              false,
              "No fingerprint template was received. Please try again."
            );
          } else {
            this.$toast.error("Empty fingerprint template received.");
          }
          return;
        }

        const metadata = {
          ...this.selectedEmployee.metadata,
          fingerprintEnrolled: true,
          fingerprintTries: 0,
          fingerprintTemplate,
          fingerprintTemplates: [fingerprintTemplate],
        };

        await publicService.updateEmployee(this.selectedEmployee.id, {
          metadata,
          organization: { id: this.location.OrganizationId },
        });

        // Reload to confirm update
        await this.getEmployeesList();
        this.isEnrolling = false;
        this.showEnrollment = false;
        if (this.supportsNativeFingerprintEnrollment) {
          window.AndroidApp.completeFingerprintEnrollmentSave(
            true,
            "Fingerprint enrollment saved."
          );
        } else {
          this.$toast.success("Fingerprint enrollment saved!");
        }
      } catch (err) {
        this.isEnrolling = false;
        const message =
          err?.response?.data?.message ||
          "The fingerprint could not be saved. Please check the connection and try again.";
        if (this.supportsNativeFingerprintEnrollment) {
          window.AndroidApp.completeFingerprintEnrollmentSave(false, message);
        } else {
          this.$toast.error(message);
        }
      }
    },
    async handlePinVerification(pin, done) {
      try {
        const valid = await publicService.verifyPin({
          EmployeeId: this.selectedEmployee.id,
          pinCode: pin,
          OrganizationId: this.location.OrganizationId,
        });

        this.returnMessage = valid.data.valid;

        if (valid?.data?.valid) {
          // ✅ PIN is correct
          this.showPinVerify = false;
          this.isEnrolling = true;

          if (window.AndroidApp?.startFingerprintEnrollment) {
            if (this.supportsNativeFingerprintEnrollment) {
              window.AndroidApp.startFingerprintEnrollment(
                this.selectedEmployee?.name || "Employee"
              );
            } else {
              this.showEnrollment = true;
              window.AndroidApp.startFingerprintEnrollment();
            }
          } else {
            this.$swal.fire({
              title: "Device Error",
              text: "Android interface not available.",
              icon: "error",
              confirmButtonColor: "#d33",
              confirmButtonText: "OK",
              position: "center",
            });
          }

          done(true);
          return;
        } else {
          // ❌ Invalid PIN entered
          await this.$swal.fire({
            title: "Invalid PIN",
            text: "The PIN you entered is incorrect. Please try again.",
            icon: "error",
            confirmButtonColor: "#d33",
            confirmButtonText: "Retry",
            position: "center",
          });
          done(false);
          return;
        }
      } catch (err) {
        const message =
          err?.response?.data?.message ||
          "PIN verification failed. Please try again.";

        await this.$swal.fire({
          title: "PIN Verification Failed",
          text: message,
          icon: "error",
          confirmButtonColor: "#d33",
          confirmButtonText: "OK",
          position: "center",
        });

        done(false);
      }
    },
    openAnyDeskSupport() {
      if (
        window.AndroidApp &&
        typeof window.AndroidApp.openAnyDesk === "function"
      ) {
        window.AndroidApp.openAnyDesk();
        return;
      }
      window.open(
        "https://play.google.com/store/apps/details?id=com.anydesk.anydeskandroid&hl=en_US"
      );
    },
    joinSocketChannels() {
      const locationCode = localStorage.getItem(
        `${process.env.VUE_APP_STORAGE_PREFIX}code`
      );
      const deviceCode = localStorage.getItem(
        `${process.env.VUE_APP_STORAGE_PREFIX}deviceCode`
      );
      const deviceName = localStorage.getItem(
        `${process.env.VUE_APP_STORAGE_PREFIX}deviceName`
      );
      // console.log("location", locationCode, deviceCode, deviceName);

      if (locationCode && deviceCode) {
        this.$socket.emit("join-location", {
          locationCode,
          deviceCode,
          deviceName,
        });
      }

      //   if (this.$store.getters['global/location']) {
      //     this.$socket.emit('join-organization', {
      //       OrganizationId: this.$store.getters['global/location'].OrganizationId,
      //     });
      //   }
    },
    switchLocation() {
      return this.$swal
        .fire({
          title: "Switch Location",
          input: "password",
          html: "Make sure you have your location code in order to get back in, to reset location type <b>switch</b> in as a password, ",
          inputPlaceholder: "Enter your password",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Submit",
          cancelButtonText: "Cancel",
          inputAttributes: {
            maxlength: 10,
            autocapitalize: "off",
            autocorrect: "off",
          },
        })
        .then((result) => {
          // console.log("resuld", result);
          if (result.isConfirmed) {
            if (result.value === "switch") {
              this.$router.push({ name: "authentication-find-location" });
            } else {
              this.$toast.warning(
                "Wrong Entry, Enter 'switch' to change the location",
                {
                  timeout: 3500,
                }
              );
            }
          }
        })
        .catch((error) => {
          this.$toast.error(error.data ? error.data.message : "Error", {
            timeout: 2000,
          });
        });
    },
    clockAction() {
      this.$router.go(0);
    },
    // getAllSetting() {
    //   return integrationService.getAll().then((response) => {
    //     console.log("setting response getall", response);
    //     this.general = response.data;
    //   });
    // },
    backString() {
      // console.log('backString', this.search)
      // this.search.slice(0,-1)
      this.search = this.search.substring(0, this.search.length - 1);
    },
    clearBtn() {
      this.snackbar = false;
      this.search = "";
    },
    alfClcik(lat) {
      this.snackbar = true;
      // console.log("lat press", this.search, lat);
      this.search += lat;
    },
    nameSegments(name) {
      if (!this.search || !name) return [{ text: name || "", match: false }];
      const start = name.toLowerCase().indexOf(this.search.toLowerCase());
      if (start === -1) return [{ text: name, match: false }];
      const end = start + this.search.length;
      return [
        { text: name.slice(0, start), match: false },
        { text: name.slice(start, end), match: true },
        { text: name.slice(end), match: false },
      ].filter((segment) => segment.text);
    },
    checkToken() {
      // const token = localStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX}token`)
      // console.log('tokendkaj;lkdjfa;lsdf', token)
      if (localStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX}token`))
        return true;
    },
    loadSetting() {
      authService.getLocation(this.locationCode).then((response) => {
        // console.log("authService.getLocation", response);
        response.data.Integrations.map((row) => {
          if (row.pubSubHook === "TERMINALTIMECLOCK") {
            this.integration = row;
            // console.log(row);
          }
        });
        // console.log("integration", this.integration);
        // console.log("response.data.metadat", response.data);
        if (response.data.metadata && response.data.metadata.customClockIn) {
          this.metadata = {
            ...this.metadata,
            ...response.data.metadata,
          };
        } else {
          // this.metadata = {
          //   ...this.metadata,
          // };
          if (
            this.integration &&
            this.integration.LocationIntegration &&
            this.integration.LocationIntegration.metadata
          ) {
            this.metadata = {
              ...this.metadata,
              ...this.integration.LocationIntegration.metadata,
            };
          }
        }
        if (
          this.metadata &&
          this.metadata.customClockIn &&
          this.metadata.customClockIn.showDepartment
        ) {
          this.showDepartmentSetting = true;
        } else {
          this.showDepartmentSetting = false;
        }

        this.checkStatus();
      });
    },
    closeSubmit() {
      // this.loadingClockIn = false;
      // this.submitDialog = false;
      this.pinDialog = false;
      this.selectedEmployee = null;
      // this.pin = "";
      this.getEmployeesList();
    },
    changePin() {
      // console.log("changePin pressed", this.changePinDialog);
      this.changePinDialog = !this.changePinDialog;
    },
    backToList() {
      this.selectedEmployee = null;
      this.action = null;
      this.pin = "";
      this.status = "";
    },
    // async setSelectedEmployee(employee) {
    //   this.selectedEmployee = employee;
    //   this.clearBtn();

    //   // 🧠 Only apply fingerprint logic if running in Android app AND device connected
    //   if (this.isAndroidApp && this.isDeviceConnected) {
    //     const metadata = employee.metadata || {};
    //     const tries = metadata.fingerprintTries || 0;

    //     // 🟡 Not enrolled yet
    //     if (!metadata.fingerprintEnrolled) {
    //       if (tries < 3) {
    //         const result = await this.$swal.fire({
    //           title: "Fingerprint Enrollment",
    //           text: `You need to enroll your fingerprints. You have ${
    //             3 - tries
    //           } attempts left. Enroll now?`,
    //           icon: "question",
    //           showCancelButton: true,
    //           confirmButtonText: "Enroll",
    //           cancelButtonText: "Later",
    //         });

    //         if (result.isConfirmed) {
    //           // 🟢 Start enrollment flow (PIN → fingerprint)
    //           this.openPinVerificationDialog();
    //         } else {
    //           // 🟠 User clicked "Later"
    //           // Increment attempt count but continue normal flow
    //           metadata.fingerprintTries = tries + 1;
    //           try {
    //             await employeeService.update(employee.id, { metadata });
    //           } catch (err) {
    //             console.warn("Failed to update fingerprint tries:", err);
    //           }

    //           this.$toast.info(
    //             `Attempt ${metadata.fingerprintTries}/3 recorded.`
    //           );
    //           this.statusCheckDialog = true; // continue as normal
    //         }
    //       } else {
    //         // 🔴 Forced enrollment after 3 skips
    //         this.$swal
    //           .fire({
    //             title: "Fingerprint Enrollment Required",
    //             text: "You must enroll your fingerprint before continuing.",
    //             icon: "warning",
    //             confirmButtonText: "Start Enrollment",
    //           })
    //           .then(() => this.openPinVerificationDialog());
    //       }
    //       return; // ✅ Stop here so we don’t re-open dialogs twice
    //     }

    //     // ✅ Already enrolled → continue to normal flow
    //     this.statusCheckDialog = true;
    //   } else {
    //     // 🌐 Browser OR no device connected → fallback to normal behavior
    //     this.statusCheckDialog = true;
    //   }
    // },
    setClockInOrOut(selected) {
      // console.log('selected employee', this.selectedEmployee, selected)
      if (selected === this.selectedEmployee.status) {
        if (selected === "clockedIn") {
          this.$swal(
            this.selectedEmployee.name,
            "You are already clocked In",
            "error"
          );
        } else {
          this.$swal(
            this.selectedEmployee.name,
            "You are already clocked Out",
            "error"
          );
        }
      } else {
        this.statusCheckDialog = !this.statusCheckDialog;
        this.openPinDialog();
      }
    },
    async setSelectedEmployee(employee) {
      this.selectedEmployee = employee;
      this.clearBtn();

      // 🧠 If running in Android app with device connected
      if (this.isAndroidApp && this.isDeviceConnected) {
        const metadata = employee.metadata || {};
        const hasFingerprint = this.isFingerprintEnrolled(metadata);

        // ✅ Case 1: Employee already has fingerprint enrolled
        if (hasFingerprint) {
          await this.$swal.fire({
            title: "Scan Your Fingerprint",
            html: `<strong>${employee.name}</strong>, please scan your fingerprint to clock in or out.`,
            icon: "info",
            timer: 5000, // auto-close after 5s
            timerProgressBar: true,
            showConfirmButton: false,
            position: "center",
          });
          // ✅ Nothing else to do — Android will handle match automatically
          return;
        }

        // 🟡 Case 2: Not enrolled yet
        const tries = metadata.fingerprintTries || 0;

        if (!metadata.fingerprintEnrolled) {
          if (tries < 3) {
            const result = await this.$swal.fire({
              title: "Fingerprint Enrollment",
              text: `You need to enroll your fingerprints. You have ${
                3 - tries
              } attempts left. Enroll now?`,
              icon: "question",
              showCancelButton: true,
              confirmButtonText: "Enroll",
              cancelButtonText: "Later",
            });

            if (result.isConfirmed) {
              // 🟢 Start enrollment flow (PIN → fingerprint)
              this.openPinVerificationDialog();
            } else {
              // 🟠 User clicked “Later” — record attempt
              metadata.fingerprintTries = tries + 1;
              try {
                await employeeService.update(employee.id, { metadata });
              } catch (err) {
                this.$toast.error(
                  err.data ? err.data.message : "Unable to record fingerprint attempt."
                );
              }
              this.$toast.info(
                `Attempt ${metadata.fingerprintTries}/3 recorded.`
              );
              this.statusCheckDialog = true; // allow manual pin entry
            }
          } else {
            // 🔴 Forced enrollment after 3 skips
            this.$swal
              .fire({
                title: "Fingerprint Enrollment Required",
                text: "You must enroll your fingerprint before continuing.",
                icon: "warning",
                confirmButtonText: "Start Enrollment",
              })
              .then(() => this.openPinVerificationDialog());
          }
          return; // ✅ Stop here
        }
      }

      // 🌐 Fallback for browser or disconnected device
      this.statusCheckDialog = true;
    },
    isFingerprintEnrolled(metadata = {}) {
      if (metadata.fingerprintEnrolled === false) return false;

      return !!(
        metadata.fingerprintTemplate || metadata.fingerprintTemplates?.[0]
      );
    },
    openPinDialog() {
      //  console.log("e,mployeee is :", employee);

      // this.selectedEmployee = employee;
      this.pinDialog = !this.pinDialog;
      // this.clearBtn();
    },
    pinClose() {
      this.pinDialog = false;
      this.selectedEmployee = null;
    },
    submitTimeClockAction(action) {
      // console.log('actionactionactionaction', action)
      this.TimeClockActionDialog = false;
      this.action = action;
      // this.status = action
      this.selectedEmployee = this.actionEmployee;
    },
    closeTimeClockAction() {
      this.TimeClockActionDialog = false;
    },
    async getEmployeeStatus() {
      return await employeeService.getEmployeeStatus(this.selectedEmployee.id);
      // if (status.data.TimeClock) {

      // }
      // console.log("result status", status);
    },
    async removeTimeClockToken() {
      if (this.$refs && this.$refs.webcam) {
        this.$refs.webcam.stop();
      }

      localStorage.removeItem(`${process.env.VUE_APP_STORAGE_PREFIX}token`);
      this.$router.push({ name: "authentication-employee-login" });
    },
    getEmployeesList() {
      const requestId = ++this.employeeListRequestId;
      if (this.locationCode) {
        return authService
          .getLocation(this.locationCode)
          .then(async (response) => {
            if (requestId !== this.employeeListRequestId) return;
            this.location = response.data;
            this.locationCode = response.data.code;
            let employee = response.data.Employees;
            // console.log("employee", employee);
            this.employees = [];
            employee.map((row) => {
              if (
                row.metadata &&
                row.metadata.timeClockLogin &&
                row.metadata.timeClockLogin.employee
              ) {
                row.TimeClocks = (row.TimeClocks || []).sort(
                  (a, b) => {
                    const aIsOpen = a.clockOutTime === null;
                    const bIsOpen = b.clockOutTime === null;
                    if (aIsOpen !== bIsOpen) return aIsOpen ? -1 : 1;
                    return b.id - a.id;
                  }
                );
                if (row.TimeClocks.length > 0) {
                  row.status = row.TimeClocks[0].status;
                } else {
                  row.status = "Never";
                }
                this.employees.push(row);
              }
            });

            // 🟢 Send lightweight employee list to Android for local matching (new single-template shape)
            // if (
            //   window.AndroidApp &&
            //   typeof window.AndroidApp.updateEmployeesFromJson === "function"
            // ) {
            //   const simplified = this.employees.map((e) => ({
            //     id: e.id,
            //     name: e.name,
            //     // 👇 send the single merged fingerprint template
            //     fingerprintTemplate: e.metadata?.fingerprintTemplate || "",
            //   }));

            //   const json = JSON.stringify(simplified);
            //   console.log(
            //     "📤 Sending employee list to Android:",
            //     simplified.length,
            //     "employees"
            //   );
            //   window.AndroidApp.updateEmployeesFromJson(json);
            // }

            if (
              window.AndroidApp &&
              typeof window.AndroidApp.loadEmployeeList === "function"
            ) {
              const simplified = this.employees.map((e) => ({
                id: e.id,
                name: e.name,
                fingerprintTemplates: [
                  e.metadata?.fingerprintEnrolled === false
                    ? ""
                    : e.metadata?.fingerprintTemplate ||
                      e.metadata?.fingerprintTemplates?.[0] ||
                      "",
                ],
              }));

              const json = JSON.stringify(simplified);

              window.AndroidApp.loadEmployeeList(json);
            }

            // this.employees.sort((a, b) => (a.name > b.name) ? 1 : (a.name === b.name) ? ((a.size > b.size) ? 1 : -1) : -1 )
            if (this.showDepartmentSetting === true) {
              const grouped = this.employees.reduce((acc, employee) => {
                const deptId = employee.DepartmentId
                  ? employee.DepartmentId
                  : "Unassigned";
                if (!acc[deptId]) {
                  acc[deptId] = {
                    departmentName: employee.Department
                      ? employee.Department.name
                      : "No department",
                    employees: [],
                  };
                }
                acc[deptId].employees.push(employee);
                return acc;
              }, {});

              const sortedDepartments = Object.values(grouped).sort((a, b) =>
                a.departmentName.localeCompare(b.departmentName)
              );

              sortedDepartments.forEach((department) => {
                department.employees.sort((a, b) =>
                  a.name.localeCompare(b.name)
                );
              });

              this.groupedEmployees = sortedDepartments;

              this.showDepartment = this.groupedEmployees.length > 0;
            }

            // this.employees.sort((a, b) => (a.name > b.name ? 1 : -1));
            // console.log("this.employees", this.employees);
            // console.log("employees", this.employees);
          })
          .catch(() => {
            this.$toast.error("Unable to load employees for this location.");
          });
      } else {
        return employeeService
          .getAll({
            limit: 50,
            offset: 0,
            local: true,
          })
          .then(async (response) => {
            let employee = response.data;
            this.employees = [];
            employee.map((row) => {
              if (
                row.metadata &&
                row.metadata.timeClockLogin &&
                row.metadata.timeClockLogin.employee
              ) {
                this.employees.push(row);
              }
            });
            this.employees.sort((a, b) => (a.name > b.name ? 1 : -1));
            // console.log("this.employees", this.employees);
          })
          .catch((error) => {
            throw error;
          });
      }
    },
    pinCodeStyle(num) {
      if (this.pin.length >= num) return "mdi-circle";
      else return "mdi-checkbox-blank-circle-outline";
    },
    selectedNum(num) {
      num = num.toString();
      this.pin = this.pin.concat(num).trim();
    },
    saveToStorage() {
      authService.setDevice(
        this.locationCode,
        this.deviceName,
        this.deviceCode
      );
    },
    async getTimeClockStatusById(employee) {
      if (employee) {
        try {
          let timeResponse = await timeClockService.getEmployeeTimeClock(
            employee.id
          );
          // console.log("timeResponse", timeResponse);
          if (timeResponse) {
            this.selectedEmployee = timeResponse.data;
            // console.log("timeResponse", timeResponse);
            if (
              timeResponse.data.TimeClocks &&
              timeResponse.data.TimeClocks.length > 0
            ) {
              this.status = timeResponse.data.TimeClocks[0].status;
            } else {
              this.status = "clockedOut";
            }
          } else {
            throw new Error("Employee Not Found");
          }
        } catch (error) {
          throw new Error(error);
        }
      }
    },
    checkStatus() {
      let vm = this;
      navigator.permissions.query({ name: "camera" }).then(function (result) {
        // vm.permission = result.state
        // return result.state

        if (result.state === "denied") {
          vm.metadata.customClockIn.storeTimeClockImage = false;
        }
      });
    },
    getImage() {
      if (this.$refs && this.$refs.webcam) {
        const video = this.$refs.webcam.$refs.video;
        if (video && video.videoWidth && video.videoHeight) {
          const maxDimension = 320;
          const scale = Math.min(
            1,
            maxDimension / Math.max(video.videoWidth, video.videoHeight)
          );
          const canvas = document.createElement("canvas");
          canvas.width = Math.round(video.videoWidth * scale);
          canvas.height = Math.round(video.videoHeight * scale);
          canvas
            .getContext("2d")
            .drawImage(video, 0, 0, canvas.width, canvas.height);

          const obj = {
            dataUrl: canvas.toDataURL("image/jpeg", 0.65),
            info: {
              name: `${this.selectedEmployee.name
                .split(" ")
                .join("")}-${new Date().getTime()}.jpg`,
            },
          };
          return obj;
        }
      }
    },
  },
  mixins: [validationMixin],
  validations: {
    selectedEmployee: {
      required,
    },
  },
};
</script>
<style scoped>
.clock-page {
  min-height: 100%;
  background:
    linear-gradient(rgba(96, 165, 250, 0.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(96, 165, 250, 0.025) 1px, transparent 1px),
    radial-gradient(circle at 76% 8%, rgba(37, 99, 235, 0.2), transparent 28rem),
    radial-gradient(circle at 18% 55%, rgba(14, 165, 233, 0.08), transparent 24rem),
    #07111f;
  background-size: 64px 64px, 64px 64px, auto, auto, auto;
  color: #e5edf8;
}

.clock-app-bar {
  border-bottom: 1px solid rgba(148, 163, 184, 0.13) !important;
  background: rgba(7, 17, 31, 0.94) !important;
  backdrop-filter: blur(14px);
}

.clock-logo {
  cursor: pointer;
}

.socket-status {
  display: flex;
  min-height: 42px;
  padding: 6px 12px;
  align-items: center;
  gap: 9px;
  border: 1px solid rgba(52, 211, 153, 0.28);
  border-radius: 999px;
  background: rgba(16, 185, 129, 0.1);
  color: #a7f3d0;
  text-align: left;
}

.socket-status__dot {
  width: 9px;
  height: 9px;
  flex: 0 0 9px;
  border-radius: 50%;
  background: #34d399;
  box-shadow: 0 0 0 rgba(52, 211, 153, 0.55);
  animation: socket-pulse 1.8s infinite;
}

.socket-status__copy strong,
.socket-status__copy small {
  display: block;
}

.socket-status__copy strong {
  font-size: 12px;
  line-height: 1.1;
}

.socket-status__copy small {
  margin-top: 2px;
  color: #6ee7b7;
  font-size: 10px;
  white-space: nowrap;
}

.socket-status--offline {
  cursor: pointer;
  border-color: rgba(251, 113, 133, 0.42);
  background: rgba(244, 63, 94, 0.13);
  color: #fecdd3;
}

.socket-status--offline .socket-status__dot {
  background: #fb7185;
  box-shadow: none;
  animation: none;
}

.socket-status--offline .socket-status__copy small {
  color: #fda4af;
}

@keyframes socket-pulse {
  0% { box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.55); }
  70% { box-shadow: 0 0 0 7px rgba(52, 211, 153, 0); }
  100% { box-shadow: 0 0 0 0 rgba(52, 211, 153, 0); }
}

.menu-button {
  flex: 0 0 48px !important;
  width: 48px !important;
  min-width: 48px !important;
  max-width: 48px !important;
  height: 48px !important;
  min-height: 48px !important;
  max-height: 48px !important;
  padding: 0 !important;
  border-radius: 50% !important;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: rgba(255, 255, 255, 0.05);
  color: #cbd5e1 !important;
  transition: 120ms ease;
}

.menu-button:hover,
.menu-button[aria-expanded="true"] {
  border-color: rgba(34, 211, 238, 0.4);
  background: rgba(34, 211, 238, 0.1);
  color: #67e8f9 !important;
}

.menu-button:active {
  transform: scale(0.96);
}

.device-status {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 11px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
  color: #94a3b8;
  font-size: 12px;
  font-weight: 600;
}

.device-status__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #64748b;
  box-shadow: 0 0 0 3px rgba(100, 116, 139, 0.14);
}

.device-status__dot--online {
  background: #34d399;
  box-shadow: 0 0 0 3px rgba(52, 211, 153, 0.14);
}

.clock-hero {
  padding: 28px 20px 22px;
}

.clock-hero--with-camera {
  display: grid;
  width: min(100%, 980px);
  margin: 0 auto;
  grid-template-columns: 250px minmax(0, 1fr);
  align-items: center;
  gap: 30px;
}

.camera-preview {
  padding: 10px;
  border: 1px solid rgba(96, 165, 250, 0.2);
  border-radius: 18px;
  background: rgba(13, 27, 45, 0.84);
  box-shadow: 0 20px 44px rgba(0, 0, 0, 0.24);
}

.camera-preview__frame {
  position: relative;
  height: 140px;
  overflow: hidden;
  border-radius: 12px;
  background: #020617;
}

.camera-preview__video {
  display: block;
  width: 100% !important;
  height: 100% !important;
  object-fit: cover;
}

.camera-preview__frame ::v-deep video,
.camera-preview__frame ::v-deep canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
  object-fit: cover;
}

.camera-preview__live {
  position: absolute;
  top: 9px;
  left: 9px;
  display: inline-flex;
  padding: 5px 8px;
  align-items: center;
  gap: 6px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 999px;
  background: rgba(2, 6, 23, 0.76);
  color: #f8fafc;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  backdrop-filter: blur(8px);
}

.camera-preview__live span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #fb7185;
  box-shadow: 0 0 0 3px rgba(251, 113, 133, 0.18);
}

.camera-preview__notice {
  display: flex;
  padding: 10px 5px 3px;
  align-items: center;
  gap: 9px;
  text-align: left;
}

.camera-preview__notice-icon {
  display: grid;
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  place-items: center;
  border-radius: 9px;
  background: rgba(34, 211, 238, 0.1);
}

.camera-preview__notice-icon .v-icon {
  color: #67e8f9 !important;
}

.camera-preview__notice strong,
.camera-preview__notice small {
  display: block;
}

.camera-preview__notice strong {
  color: #e2e8f0;
  font-size: 11px;
}

.camera-preview__notice small {
  margin-top: 2px;
  color: #7f91a9;
  font-size: 9px;
  line-height: 1.3;
}

.camera-capture-hidden {
  position: fixed;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

.clock-hero__content {
  max-width: 680px;
  margin: 0 auto;
  text-align: center;
}

.clock-hero__eyebrow {
  display: inline-flex;
  align-items: center;
  padding: 7px 12px;
  border: 1px solid rgba(96, 165, 250, 0.2);
  border-radius: 999px;
  background: rgba(15, 30, 51, 0.72);
  color: #bfdbfe;
  font-size: 13px;
  font-weight: 600;
}

.clock-hero__time {
  margin-top: 10px;
  background: linear-gradient(120deg, #f8fafc 25%, #93c5fd 100%);
  background-clip: text;
  color: transparent;
  -webkit-background-clip: text;
  font-size: clamp(42px, 6vw, 68px);
  font-weight: 750;
  letter-spacing: -3px;
  line-height: 1.05;
}

.clock-hero__date {
  margin-top: 5px;
  color: #94a3b8;
  font-size: 15px;
}

.clock-hero__hint {
  margin-top: 16px;
  color: #cbd5e1;
  font-size: 14px;
  font-weight: 600;
}

.employee-status-summary {
  display: grid;
  width: min(calc(100% - 24px), 1040px);
  margin: 0 auto 8px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.employee-status-summary__item {
  display: flex;
  padding: 9px 13px;
  align-items: center;
  gap: 10px;
  border: 1px solid;
  border-radius: 13px;
  backdrop-filter: blur(10px);
}

.employee-status-summary__item--total {
  border-color: rgba(96, 165, 250, 0.3);
  background: rgba(37, 99, 235, 0.1);
  color: #93c5fd;
}

.employee-status-summary__item--in {
  border-color: rgba(52, 211, 153, 0.3);
  background: rgba(16, 185, 129, 0.1);
  color: #6ee7b7;
}

.employee-status-summary__item--out {
  border-color: rgba(251, 113, 133, 0.3);
  background: rgba(244, 63, 94, 0.1);
  color: #fda4af;
}

.employee-status-summary__icon {
  display: grid;
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  place-items: center;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.07);
}

.employee-status-summary__item .v-icon {
  color: currentColor !important;
}

.employee-status-summary__item strong,
.employee-status-summary__item small {
  display: block;
}

.employee-status-summary__item strong {
  color: #f8fafc;
  font-size: 18px;
  line-height: 1;
}

.employee-status-summary__item small {
  margin-top: 3px;
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
}

.employee-directory {
  max-width: 1040px;
}

.employee-panel {
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.14) !important;
  background: rgba(10, 22, 39, 0.88) !important;
  box-shadow: 0 22px 54px rgba(0, 0, 0, 0.28) !important;
  backdrop-filter: blur(14px);
}

.employee-list {
  background: transparent !important;
}

.department-section + .department-section {
  margin-top: 18px;
}

.department-heading {
  display: flex;
  min-height: 64px;
  margin: 4px 0 9px;
  padding: 10px 14px;
  align-items: center;
  gap: 12px;
  border: 1px solid rgba(96, 165, 250, 0.17);
  border-radius: 14px;
  background: linear-gradient(
      90deg,
      rgba(37, 99, 235, 0.12),
      rgba(34, 211, 238, 0.035)
    ),
    rgba(13, 27, 45, 0.92);
}

.department-heading__icon {
  display: grid;
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  place-items: center;
  border: 1px solid rgba(34, 211, 238, 0.18);
  border-radius: 11px;
  background: rgba(34, 211, 238, 0.09);
}

.department-heading__icon .v-icon {
  color: #67e8f9 !important;
}

.department-heading__copy {
  min-width: 0;
}

.department-heading__copy small,
.department-heading__copy strong {
  display: block;
}

.department-heading__copy small {
  color: #64748b;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 1.1px;
  text-transform: uppercase;
}

.department-heading__copy strong {
  overflow: hidden;
  color: #e5edf8;
  font-size: 16px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.department-heading__count {
  margin-left: auto;
  padding: 6px 10px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
  color: #94a3b8;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.employee-row {
  min-height: 74px;
  margin-bottom: 7px;
  border: 1px solid transparent;
  border-radius: 14px;
  transition: background 160ms ease, border-color 160ms ease, transform 160ms ease;
}

.employee-row.even-background,
.employee-row.odd-background {
  border-color: rgba(148, 163, 184, 0.08);
  background: rgba(255, 255, 255, 0.025);
}

.employee-row ::v-deep .v-list-item__title {
  color: #e5edf8;
}

.employee-row ::v-deep .v-list-item__subtitle,
.employee-row ::v-deep .v-list-item__action-text {
  color: #94a3b8 !important;
}

.employee-row ::v-deep .v-list-item__avatar {
  background: rgba(96, 165, 250, 0.1) !important;
}

.employee-row
  ::v-deep
  .fingerprint-avatar.fingerprint-avatar--enrolled {
  border: 1px solid rgba(52, 211, 153, 0.28);
  background: rgba(16, 185, 129, 0.14) !important;
  box-shadow: inset 0 0 0 1px rgba(52, 211, 153, 0.05);
}

.employee-row
  ::v-deep
  .fingerprint-avatar--enrolled
  .v-icon {
  color: #34d399 !important;
  filter: drop-shadow(0 0 5px rgba(52, 211, 153, 0.25));
}

.employee-row
  ::v-deep
  .fingerprint-avatar.fingerprint-avatar--inactive {
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: rgba(100, 116, 139, 0.1) !important;
}

.employee-row
  ::v-deep
  .fingerprint-avatar--inactive
  .v-icon {
  color: #94a3b8 !important;
}

.employee-row:hover {
  z-index: 1;
  border-color: rgba(96, 165, 250, 0.38);
  background: rgba(37, 99, 235, 0.1) !important;
  transform: translateY(-1px);
}

.employee-row:active {
  transform: scale(0.995);
}

.alphabet-rail {
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.14);
  border-radius: 16px;
  background: rgba(10, 22, 39, 0.88);
}

.alphabet-rail ::v-deep .v-list {
  background: transparent !important;
}

.alphabet-rail ::v-deep .v-list-item__content,
.alphabet-rail ::v-deep .v-list-item__title {
  color: inherit;
}

.alphabet-rail__item {
  min-height: 36px;
  color: #94a3b8 !important;
  font-size: 13px;
  font-weight: 700;
}

.alphabet-rail__item:hover {
  color: #93c5fd !important;
  background: rgba(37, 99, 235, 0.12);
}

.alphabet-rail__item.alphabet-letter--active {
  background: rgba(37, 99, 235, 0.2);
  color: #bfdbfe !important;
  box-shadow: inset 3px 0 #60a5fa;
}

.alphabet-search {
  margin: 10px 12px 4px;
}

.alphabet-strip {
  display: flex;
  width: 100%;
  padding: 4px;
  overflow-x: auto;
  border: 1px solid rgba(148, 163, 184, 0.14);
  border-radius: 14px;
  background: rgba(10, 22, 39, 0.94) !important;
}

.alphabet-strip button {
  flex: 1 0 42px;
  min-width: 42px;
  height: 42px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: #94a3b8;
  font: inherit;
  font-size: 13px;
  font-weight: 800;
  touch-action: manipulation;
}

.alphabet-strip button.alphabet-letter--active {
  background: rgba(37, 99, 235, 0.24);
  color: #bfdbfe;
  box-shadow: inset 0 0 0 1px rgba(96, 165, 250, 0.34);
}

.search-feedback {
  align-items: center;
  display: flex;
  gap: 8px;
  margin-top: 7px;
  min-height: 40px;
  padding: 6px 8px 6px 12px;
  border: 1px solid rgba(96, 165, 250, 0.24);
  border-radius: 12px;
  background: rgba(37, 99, 235, 0.1);
  color: #94a3b8;
  font-size: 12px;
}

.search-feedback > span:first-child {
  flex: 1;
}

.search-feedback strong,
.search-feedback .v-icon {
  color: #93c5fd !important;
}

.search-feedback button {
  align-items: center;
  background: rgba(255, 255, 255, 0.06);
  border: 0;
  border-radius: 8px;
  display: flex;
  height: 30px;
  justify-content: center;
  width: 30px;
}

.search-feedback button .v-icon {
  color: #cbd5e1 !important;
}

.name-match {
  background: rgba(251, 191, 36, 0.2);
  border-radius: 4px;
  box-shadow: inset 0 -2px rgba(251, 191, 36, 0.7);
  color: #fde68a;
  padding: 0 1px;
}

.action-dialog {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 24px !important;
  background:
    radial-gradient(circle at 80% 0%, rgba(37, 99, 235, 0.18), transparent 18rem),
    #0b1729 !important;
  color: #e5edf8;
}

.action-dialog__close {
  position: absolute;
  z-index: 2;
  top: 14px;
  right: 14px;
  background: rgba(255, 255, 255, 0.07);
  color: #cbd5e1 !important;
}

.action-dialog__eyebrow {
  color: #60a5fa;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.action-dialog__title {
  margin-top: 5px;
  padding-right: 38px;
  color: #f8fafc;
  font-size: 28px;
  font-weight: 750;
  letter-spacing: -0.7px;
  line-height: 1.2;
  text-transform: capitalize;
}

.action-dialog__subtitle {
  margin-top: 5px;
  color: #94a3b8;
  font-size: 15px;
}

.clock-action {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 104px;
  gap: 12px;
  padding: 16px;
  border: 1px solid;
  border-radius: 18px;
  text-align: left;
  transition: transform 150ms ease, box-shadow 150ms ease;
}

.clock-action:hover {
  transform: translateY(-2px);
}

.clock-action:active {
  transform: scale(0.98);
}

.clock-action--in {
  border-color: rgba(52, 211, 153, 0.3);
  background: rgba(16, 185, 129, 0.09);
  color: #6ee7b7;
}

.clock-action--out {
  border-color: rgba(251, 113, 133, 0.3);
  background: rgba(244, 63, 94, 0.09);
  color: #fda4af;
}

.clock-action__icon {
  display: grid;
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.07);
}

.clock-action strong,
.clock-action small {
  display: block;
}

.clock-action strong {
  font-size: 17px;
}

.clock-action small {
  margin-top: 3px;
  opacity: 0.7;
}

.clock-action__arrow {
  margin-left: auto;
  color: currentColor !important;
}

.noscroll {
  overflow-y: scroll;
}

div::-webkit-scrollbar {
  display: none;
  /* for Chrome, Safari, and Opera */
}

.touch-view-container {
  height: calc(100vh - 300px);
  width: 100%;
  overflow: auto;
}

.touch-view-container-big {
  height: calc(100vh - 280px);
  width: 100%;
  overflow: auto;
}

.swal2-popup.clockin-popup {
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(46, 125, 50, 0.3);
}

.swal2-popup.clockout-popup {
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(173, 20, 87, 0.3);
}

@media (max-width: 600px) {
  .socket-status__copy small {
    display: none;
  }

  .clock-hero {
    padding: 18px 12px 12px;
  }

  .clock-hero__time {
    letter-spacing: -2px;
  }

  .clock-hero__hint {
    margin-top: 10px;
  }

  .employee-row {
    min-height: 68px;
  }

  .action-dialog {
    border-radius: 20px !important;
  }

  .clock-action {
    min-height: 78px;
  }
}

@media (max-width: 760px) {
  .clock-hero--with-camera {
    grid-template-columns: 190px minmax(0, 1fr);
    gap: 16px;
  }

  .camera-preview__frame {
    height: 112px;
  }

  .camera-preview__notice small {
    display: none;
  }
}

@media (max-width: 480px) {
  .clock-hero--with-camera {
    display: flex;
    flex-direction: column-reverse;
  }

  .camera-preview {
    width: min(100%, 340px);
  }

  .employee-status-summary {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .employee-status-summary__item {
    min-width: 0;
  }
}
</style>

<style>
.home-settings-menu {
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 18px !important;
  background: #0d1b2d;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.42) !important;
}

.home-settings-menu .home-menu-list {
  padding: 10px !important;
  background: linear-gradient(
      145deg,
      rgba(37, 99, 235, 0.09),
      transparent 45%
    ),
    #0d1b2d !important;
}

.home-menu-heading {
  display: flex;
  padding: 9px 10px 13px;
  align-items: center;
  gap: 11px;
}

.home-menu-heading__icon {
  display: grid;
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  place-items: center;
  border: 1px solid rgba(34, 211, 238, 0.2);
  border-radius: 11px;
  background: rgba(34, 211, 238, 0.09);
}

.home-menu-heading__icon .v-icon {
  color: #67e8f9 !important;
}

.home-menu-heading strong,
.home-menu-heading small {
  display: block;
}

.home-menu-heading strong {
  color: #f8fafc;
  font-size: 15px;
}

.home-menu-heading small {
  margin-top: 2px;
  color: #7f91a9;
  font-size: 11px;
}

.home-settings-menu .home-menu-divider {
  margin: 0 4px 8px;
  border-color: rgba(148, 163, 184, 0.14) !important;
}

.home-settings-menu .home-menu-item {
  display: flex;
  min-height: 72px;
  margin-top: 5px;
  padding: 8px 10px;
  border: 1px solid transparent;
  border-radius: 13px;
  align-items: center;
  gap: 13px;
  transition: 120ms ease;
}

.home-settings-menu .home-menu-item:hover,
.home-settings-menu .home-menu-item:focus {
  border-color: rgba(34, 211, 238, 0.18);
  background: rgba(34, 211, 238, 0.07) !important;
}

.home-settings-menu .home-menu-item:active {
  transform: scale(0.985);
}

.home-settings-menu .home-menu-item__icon {
  display: grid;
  width: 42px;
  height: 42px;
  min-width: 42px;
  margin: 0 !important;
  place-items: center;
  border-radius: 12px;
  background: rgba(37, 99, 235, 0.13);
}

.home-settings-menu .home-menu-item__icon .v-icon {
  color: #60a5fa !important;
}

.home-settings-menu .home-menu-item__copy {
  min-width: 0;
  flex: 1 1 auto;
}

.home-settings-menu .home-menu-item__copy strong,
.home-settings-menu .home-menu-item__copy small {
  display: block;
}

.home-settings-menu .home-menu-item__copy strong {
  color: #e8eef7 !important;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
}

.home-settings-menu .home-menu-item__copy small {
  overflow: hidden;
  margin-top: 4px;
  color: #8192a9 !important;
  font-size: 11px;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.home-settings-menu .v-list-item__action {
  margin-left: 8px !important;
}

.home-settings-menu .v-list-item__action .v-icon {
  color: #64748b !important;
}
</style>
